import { useCallback, useState } from "react";
import { Select, InputNumber } from "antd";
import { ColumnHaving, Comparator, Condition } from "src/search/types";
import { relativeUnitsFor } from "src/utils/strings";
import { Flex } from "src/components/Flex";

function UnitInputNumber({
  value,
  placeholder,
  options,
  defaultValue,
  onChange,
}: {
  value: number | null;
  placeholder: string;
  options: { label: string; value: number }[];
  defaultValue: { label: string; value: number };
  onChange: (n: number | null) => void;
}) {
  // figure a unit better suited than the default unit if possible - taken as "the largest option that divides the value"
  let largestDivisorOption =
    value != null &&
    options
      .sort((o1, o2) => o2.value - o1.value)
      .find((o) => value % o.value == 0);
  const selectedOption =
    value === 0 ? defaultValue : largestDivisorOption || defaultValue;

  const [multiplier, setMultiplier] = useState(selectedOption.value);
  const [val, setVal] = useState(value ? value / multiplier : null);

  const changeNumber = useCallback(
    (n: number | null, multiplier: number) => {
      setVal(n);
      setMultiplier(multiplier);
      if (n == null) {
        onChange(null);
      } else {
        onChange(n * multiplier);
      }
    },
    [onChange]
  );

  return (
    <Flex>
      <InputNumber
        size="small"
        style={{
          fontSize: 12,
          width: 50,
          backgroundColor: "var(--color-bg-layout)",
          padding: 8,
          borderRadius: 4,
        }}
        bordered={false}
        value={val}
        placeholder={placeholder}
        onChange={(n) => changeNumber(n, multiplier)}
      />
      <Select
        defaultValue={selectedOption}
        style={{ fontSize: 12, minWidth: 50, padding: 8, lineHeight: 12 }}
        dropdownStyle={{ padding: 0 }}
        size="small"
        options={options}
        onChange={(item) => {
          changeNumber(val, item as any);
        }} // not sure why ts insists that {label, value} is passed back when it is actually value
      />
    </Flex>
  );
}

function strToNumber(s: string): number | null {
  if (s == "__VZBL::null__") return null;
  try {
    return Number(s);
  } catch {
    return null;
  }
}

export function NumbersInput<T = Condition | ColumnHaving>({
  condition,
  onChange,
  values,
  unit,
}: {
  values: string[];
  onChange(cond: T): void;
  condition: T;
  unit: string | undefined;
}) {
  const unitInfo = relativeUnitsFor(unit || "");

  const [lower, upper] = values.map(strToNumber);

  const changeRange = useCallback(
    (range: (number | null)[]) => {
      const newRange = range.map((n) =>
        n == null ? "__VZBL::null__" : `${n}`
      );
      onChange({
        ...condition,
        comparison: { WITHIN: newRange } as Comparator,
      });
    },
    [onChange, condition]
  );

  if (unitInfo) {
    const { qty, options } = unitInfo;
    const selectOptions = options.map((o) => ({
      label: o.label,
      value: o.qty / qty,
    }));
    const defaultOption = selectOptions[0];

    return (
      <Flex>
        <UnitInputNumber
          value={lower}
          placeholder="No min"
          defaultValue={defaultOption}
          options={selectOptions}
          onChange={(n: number | null) => {
            changeRange([n, upper]);
          }}
        />
        <UnitInputNumber
          value={upper}
          placeholder="No max"
          defaultValue={defaultOption}
          options={selectOptions}
          onChange={(n: number | null) => {
            changeRange([lower, n]);
          }}
        />
      </Flex>
    );
  } else {
    return (
      <Flex style={{ padding: 2 }}>
        <InputNumber
          size="small"
          style={{ fontSize: 12, width: 64 }}
          bordered={false}
          value={lower}
          placeholder="No min"
          onChange={(num) => changeRange([num, upper])}
        />
        <InputNumber
          size="small"
          style={{ fontSize: 12, width: 64 }}
          bordered={false}
          value={upper}
          placeholder="No max"
          onChange={(num) => changeRange([lower, num])}
        />
      </Flex>
    );
  }
}
